import { useEffect, useRef, useState } from 'react';

/**
 * Teclado numerico de 4 casilleros.
 *
 * Es propio y no un <input type="number"> porque esto se usa parado en la
 * cancha, de noche, con una mano: los botones del sistema son chicos y el
 * teclado del celular tapa media pantalla. Al cuarto digito entra solo, sin
 * que haya que apretar "aceptar".
 */
export function TecladoPin({
  onCompleto,
  ocupado,
  etiqueta = 'Tu PIN',
  paso,
  reiniciar,
}: {
  onCompleto: (pin: string) => void;
  ocupado?: boolean;
  etiqueta?: string;
  paso?: number;
  /** Cambiar este numero borra lo tipeado (por ejemplo, tras un PIN erroneo). */
  reiniciar?: number;
}) {
  const [pin, setPin] = useState('');
  const escrito = useRef('');

  useEffect(() => { escrito.current = ''; setPin(''); }, [reiniciar]);

  const tocar = (tecla: string) => {
    if (ocupado) return;
    const anterior = escrito.current;
    if (tecla !== 'borrar' && anterior.length >= 4) return;
    const siguiente = tecla === 'borrar' ? anterior.slice(0, -1) : anterior + tecla;
    escrito.current = siguiente;
    setPin(siguiente);
    // Solo un toque puede enviar: desbloquear o cambiar de paso nunca reenvía.
    if (siguiente.length === 4) onCompleto(siguiente);
  };

  return (
    <div className="flex flex-col items-center gap-6">
      {paso !== undefined && (
        <span className="-mb-3.5 text-sm text-(--color-acento)" aria-live="polite">
          Paso {paso} de 3
        </span>
      )}
      <p className="text-center text-lg leading-snug font-semibold">{etiqueta}</p>

      <div className="flex gap-3" aria-label={`${pin.length} de 4 digitos`}>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-4 w-4 rounded-full border-2 transition
              ${i < pin.length ? 'border-(--color-acento) bg-(--color-acento)' : 'border-(--color-borde)'}`}
          />
        ))}
      </div>

      <div className="grid w-full max-w-xs grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'borrar'].map((tecla, i) =>
          tecla === '' ? (
            <span key={i} />
          ) : (
            <button
              key={i}
              onClick={() => tocar(tecla)}
              disabled={ocupado}
              aria-label={tecla === 'borrar' ? 'Borrar último número' : undefined}
              className="min-h-17 rounded-2xl border border-(--color-borde) bg-(--color-panel)
                text-[26px] transition active:scale-95 disabled:opacity-40"
            >
              {tecla === 'borrar' ? '⌫' : tecla}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
